import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { FlaskConical } from "lucide-react";

interface SandboxRow {
  id: string;
  created_at: string;
  group_id: string;
  skill_name: string | null;
  input_text: string;
  v19_output: string | null;
  v19_status: string | null;
  v19_match_score: number | null;
  legacy_output: string | null;
  legacy_would_respond: boolean | null;
  latency_ms: number | null;
  metadata: any;
}

export default function Sandbox() {
  const { data: groups } = useQuery({
    queryKey: ["sandbox-groups"],
    queryFn: async () => {
      const { data } = await (supabase as any)
        .from("syra_groups")
        .select("id, name")
        .eq("sandbox_mode", true);
      return (data ?? []) as Array<{ id: string; name: string }>;
    },
  });

  const { data: rows, isLoading } = useQuery({
    queryKey: ["sandbox-executions"],
    queryFn: async () => {
      const { data } = await supabase
        .from("sandbox_executions" as any)
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100);
      return (data ?? []) as unknown as SandboxRow[];
    },
    refetchInterval: 10000,
  });

  // Agrupa por message_id (ou created_at) para casar v19 + legacy
  const grouped = new Map<string, { v19?: SandboxRow; legacy?: SandboxRow; key: string }>();
  for (const r of rows ?? []) {
    const k = `${r.group_id}|${r.input_text}|${r.created_at.slice(0, 16)}`;
    const cur = grouped.get(k) ?? { key: k };
    if (r.v19_output !== null || r.v19_status) cur.v19 = r;
    if (r.legacy_output !== null) cur.legacy = r;
    grouped.set(k, cur);
  }
  const turns = Array.from(grouped.values()).slice(0, 50);
  const groupName = (id: string) => groups?.find((g) => g.id === id)?.name ?? id.slice(0, 8);

  return (
    <div className="space-y-6 p-6 max-w-7xl mx-auto">
      <header className="flex items-start gap-3">
        <div className="p-2 rounded-lg bg-warning/10 text-warning">
          <FlaskConical className="w-6 h-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Sandbox</h1>
          <p className="text-sm text-muted-foreground">
            Grupos com modo sandbox ligado não enviam respostas no WhatsApp. Aqui você compara o que a skill v19 geraria vs o que o caminho legado responderia.
          </p>
        </div>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Grupos em sandbox</CardTitle>
        </CardHeader>
        <CardContent>
          {!groups?.length ? (
            <p className="text-sm text-muted-foreground">
              Nenhum grupo está em modo sandbox. Vá em <code>/groups</code> → Configurar e ligue o switch "Modo Sandbox".
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {groups.map((g) => (
                <Badge key={g.id} variant="outline">{g.name}</Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Últimos turnos</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {isLoading ? (
            <Skeleton className="h-32 w-full" />
          ) : turns.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sem execuções registradas ainda.</p>
          ) : (
            turns.map((t) => {
              const sample = t.v19 ?? t.legacy!;
              return (
                <div key={t.key} className="rounded-lg border border-border p-4 space-y-3">
                  <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
                    <span>
                      {new Date(sample.created_at).toLocaleString()} · {groupName(sample.group_id)}
                      {sample.skill_name && <> · skill <code>{sample.skill_name}</code></>}
                    </span>
                    {t.v19?.v19_status && (
                      <Badge variant={t.v19.v19_status === "success" ? "default" : "secondary"} className="text-xs">
                        v19: {t.v19.v19_status}
                      </Badge>
                    )}
                  </div>

                  <div>
                    <div className="text-xs uppercase text-muted-foreground mb-1">Mensagem do usuário</div>
                    <div className="text-sm bg-muted/50 rounded p-2 whitespace-pre-wrap">{sample.input_text}</div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <div className="text-xs uppercase text-muted-foreground flex items-center justify-between">
                        <span>Skill v19 proporia</span>
                        {t.v19?.v19_match_score != null && (
                          <span>match {t.v19.v19_match_score.toFixed(2)}</span>
                        )}
                      </div>
                      <div className="text-sm rounded p-2 border border-primary/20 bg-primary/5 min-h-[60px] whitespace-pre-wrap">
                        {t.v19?.v19_output ?? <span className="text-muted-foreground italic">— sem proposta v19</span>}
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="text-xs uppercase text-muted-foreground">Caminho legado responderia</div>
                      <div className="text-sm rounded p-2 border border-warning/20 bg-warning/5 min-h-[60px] whitespace-pre-wrap">
                        {t.legacy?.legacy_output ?? <span className="text-muted-foreground italic">— sem resposta do legado</span>}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>
    </div>
  );
}
